import { IDirective, IDirectiveFactory, IScope } from "angular";
import dayjs from "@gtmhub/dayjs";
import Chartist, { ILineChartOptions } from "chartist";
import { GoalService } from "@gtmhub/goals/goalService";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { IConfidenceMapping } from "@gtmhub/core";
import { IMetric, IMetricSnapshot } from "@gtmhub/okrs/metrics";
import { IGoal } from "@gtmhub/goals/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";

interface IConfidenceLevelTrendChartScope extends IScope {
  goal: IGoal;
  metric: IMetric;
  id: string;
}

interface IConfidenceValueMap {
  color?: string;
  confidenceName?: string;
  emojiClass?: string;
}

export class ConfidenceLevelTrendChart implements IDirective {
  scope = {
    metric: "=",
    goal: "=",
    id: "@",
  };
  restrict = "E";
  transclude = true;
  template = "<div id='metric-confidence-level-trend-{{id}}' class='metric-confidence-level-trend-chart'></div>";

  constructor(private $rootScope: IGtmhubRootScopeService, private goalService: GoalService, private accountService: AccountService) {}

  // eslint-disable-next-line sonarjs/cognitive-complexity
  link(scope: IConfidenceLevelTrendChartScope): void {
    if (!scope.metric.metricHistory || scope.metric.metricHistory.length === 0) {
      return;
    }

    const dateFormat = {
      ddmmm: "DD MMM",
      ddmmmyy: "DD MMM YY",
      ddmmmyyyy: "DD MMM YYYY",
    };

    // calculating biggest number on Y axis length
    const targetValueLength = 5;
    // calculating offset needed to display any value properly
    const yAxisOffset = 25 + (targetValueLength - 1) * 5;

    const data = {
      labels: [],
      series: [],
    };

    const progressIndex = 0;
    data.series[progressIndex] = {
      // name: "progress",
      data: [],
    };

    const latestValueIndex = 1;
    data.series[latestValueIndex] = {
      // name: "latest",
      data: [],
    };

    // object to identify the correct date/index where to place the check-in value
    const dateDataIndexMap: { [date: string]: number } = {};

    // +1 added to difference to include last day of the period
    const duration: number = dayjs(scope.goal.dateTo).diff(scope.goal.dateFrom, "days") + 1;

    const processLabelsAndItsMap = (): void => {
      // setting labels and date-index map
      for (let i = 0; i < duration; i++) {
        const dateToAdd: string = dayjs(scope.goal.dateFrom).add(i, "days").format(dateFormat.ddmmmyyyy);
        const dateToAddLabel = dayjs(scope.goal.dateFrom).add(i, "days").format();

        dateDataIndexMap[dateToAdd] = i;

        data.labels.push(dateToAddLabel);
      }
    };

    const processProgressLine = (): void => {
      // setting values of updates w/o gaps (missing dates b/n check-ins)
      for (const record of scope.metric.metricHistory) {
        const dateOfUpdate: string = dayjs(record.updatedAt).format(dateFormat.ddmmmyyyy);

        // getting index from the map to gain constant access to array entry
        const index: number = dateDataIndexMap[dateOfUpdate];
        // and assigning it
        data.series[progressIndex].data[index] = record.confidenceValue;
      }
    };

    const processLatestPoint = (): void => {
      // latest update snapshot of the metric (check-in)
      const snapshotCount: number = scope.metric.metricHistory.length;

      // use the last update to draw the point("latest")
      const latestSnapshot: IMetricSnapshot = scope.metric.metricHistory[snapshotCount - 1];
      const dateOfUpdate: string = dayjs(latestSnapshot.updatedAt).format(dateFormat.ddmmmyyyy);
      const index: number = dateDataIndexMap[dateOfUpdate];

      data.series[latestValueIndex].data[index] = latestSnapshot.confidenceValue;
    };

    // getting and setting the confidence settings on root level
    this.accountService.checkConfidenceSettings();
    // getting confidence scale - 'minor'/ 'major' (it's not in confidence settings)
    const confidenceScale = this.accountService.getAccountSetting("confidenceScale");

    // scale factor of minor/major confidence scale
    const scaleFactor = 10;

    // sorting mappings to be sure they will be sorted in decreasing order
    const mappings: IConfidenceMapping[] = angular
      .copy(this.$rootScope.confidenceSettings.confidenceMapping)
      .sort((a: IConfidenceMapping, b: IConfidenceMapping) => b.range.to - a.range.to);

    const findRange = (confidenceValue: number): IConfidenceMapping => {
      // if no confidence is set yet --> return null and check for it in setViewVariables
      if (confidenceValue === null) {
        return null;
      }

      // mappings are sorted --> checking if our confidence value is greater than lower boundaries of each mapping
      for (const mapping of mappings) {
        if (confidenceValue > mapping.range.from && confidenceValue <= mapping.range.to) {
          return mapping;
        }
      }

      // zero is inclusive so if our value is zero we return the last mapping with lowest ranges
      return mappings[mappings.length - 1];
    };

    const formatLabel = (value): number => {
      return confidenceScale === "major" ? value * scaleFactor : value;
    };

    const getConfidenceValueMap = (mapping: IConfidenceMapping, confidenceValue: number) => {
      const confidenceValueMap: IConfidenceValueMap = {};
      // if mapping is null we display nothing
      if (!mapping) {
        return null;
      }

      switch (this.$rootScope.confidenceSettings.confidenceType) {
        case "numeric":
          confidenceValueMap.confidenceName = confidenceScale !== "major" ? confidenceValue.toString() : confidenceValue * scaleFactor + "/10";
          confidenceValueMap.color = mapping.color;
          break;

        case "text":
          confidenceValueMap.confidenceName = mapping.name;
          confidenceValueMap.color = mapping.color;
          break;

        case "emoji":
          // emoji class is added to span element inside chartist-tooltip to show emoji on chart
          confidenceValueMap.emojiClass = "emoji-inline twa twa-" + mapping.name;
          confidenceValueMap.color = mapping.color;
          break;

        default:
          break;
      }

      return confidenceValueMap;
    };

    const configureChartParamsAndDraw = (): void => {
      // calculating year difference of session's start and end
      const yearDifference: number = dayjs(scope.goal.dateTo).diff(scope.goal.dateFrom, "years");
      // setting date format to display year if session starts and ends in different years
      const dateFormatString = yearDifference > 0 ? dateFormat.ddmmmyy : dateFormat.ddmmm;

      // interval of labels placed on x axis
      const interval: number = Math.round(duration / 4);

      processLabelsAndItsMap();

      processProgressLine();

      processLatestPoint();

      const chartWidth = 460;
      const chartPadding = {
        top: 15,
        right: 50,
        bottom: 5,
        left: 10,
      };

      const options: ILineChartOptions = {
        width: `${chartWidth}px`,
        chartPadding: chartPadding,
        high: 1,
        low: 0,
        lineSmooth: false,
        plugins: [
          Chartist.plugins.tooltip({
            tooltipFncJsonParse: true,
            tooltipFnc: (meta: string, value: number) => {
              const confidenceValueMap = getConfidenceValueMap(findRange(value), value);
              return JSON.stringify(confidenceValueMap);
            },
          }),
        ],
        axisX: {
          labelInterpolationFnc: function skipLabels(value, index) {
            // include last date goal.dateTo in x axis
            if (index + 1 === duration) {
              const date = new Date(value);
              return dayjs(date).format(dateFormatString);
            }

            if (index % interval === 0) {
              const date = new Date(value);
              return dayjs(date).format(dateFormatString);
            }

            return null;
          },
        },
        axisY: {
          // the offset of the labels to the chart area
          offset: yAxisOffset,
          // position where labels are placed. Can be set to `start` or `end` where `start` is equivalent to left or top on vertical axis
          // and `end` is equivalent to right or bottom on horizontal axis.
          position: "start",
          labelInterpolationFnc: (value, index, hLabels): number => {
            if (index === 0 || index === hLabels.length - 1) {
              return formatLabel(value);
            }
            return null;
          },
        },
      };

      const containerId = `metric-confidence-level-trend-${scope.id}`;
      setTimeout(() => {
        const container = document.getElementById(containerId);
        if (container) {
          new Chartist.Line(container, data, options);
        }
      }, 100);
    };

    if (!scope.goal) {
      const goalFields = "dateFrom,dateTo,id,name,ownerIds,sessionId";

      this.goalService.getGoalWithFields(scope.metric.goalId, goalFields).then(
        (goal) => {
          scope.goal = goal;

          configureChartParamsAndDraw();
        },
        (error) => {
          console.error(error);
        }
      );
    } else {
      configureChartParamsAndDraw();
    }
  }

  static factory(): IDirectiveFactory {
    const directive = ($rootScope: IGtmhubRootScopeService, goalService: GoalService, accountService: AccountService) =>
      new ConfidenceLevelTrendChart($rootScope, goalService, accountService);
    directive.$inject = ["$rootScope", "GoalService", "AccountService"];
    return directive;
  }
}
